import React, { Component } from 'react';
import { connect } from 'react-redux';
import posed from 'react-pose';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { toast } from 'react-toastify';

import { messages } from 'config/messages';
import { numpad as colors, modal } from 'config/paletteConfig';

import * as Immutable from 'immutable';
import { CoordinatePoint } from 'types/point';
import { Direction } from 'types/direction';
import { Wall, CircleWall } from 'types/wall';

import { RootState } from 'reducers/rootReducer';
import { UnreachableCaseError } from 'helpers/UnreachableCaseError';
import { parseString } from 'helpers/draw/parseDrawInput';
import { movePointInDirection } from 'helpers/move/movePoint';
import { feetToPixels, metersToPixels } from 'helpers/pixelsToDistance';

import { selectors as drawsSelectors, actions as drawActions } from 'ducks/draw/draw';
import { actions as editModeActions, selectors as editModeSelectors } from 'ducks/editMode';
import { selectors as wallSelectors } from 'ducks/model/walls';
import { selectors as numpadModalSelectors, actions as numpadModalActions } from 'ducks/modal/numpadModal';
import { selectors as settingsSelectors } from 'ducks/settings';
import { selectors as drawWithKeypadSelectors, actions as drawWithKeypadActions } from 'ducks/draw/drawWithKeypad';

import { Modal, ButtonModalHeader as Button } from 'components/modal/Modal';
import { appearFromTop } from 'components/animations';
import { scrollbar } from 'components/globalStyles';
import { ReactComponent as IconBackspace } from 'assets/icons/backspace.svg';
import { ReactComponent as IconClose } from 'assets/icons/x.svg';
import { ReactComponent as IconNumpadShow } from 'assets/icons/numpad-show.svg';
import { ReactComponent as IconNumpadHide } from 'assets/icons/numpad-hide.svg';
import { coordinatesByAngle } from 'helpers/geometry';
import { isMobileApplication } from 'effects/auth';
import { actions as messageContainerActions, onSubmitEventType } from 'ducks/messageContainer';
import { actions as removeObjectsActions } from 'ducks/remove/removeObjects';
import { actions as historyActions } from 'ducks/history/history';



const MAX_LENGTH = 8;

const NumpadContainer = styled.div`
  display: flex;
  align-items: baseline;
  padding: 10px;
`;

const NumbersContainer = styled.div`
  display: flex;
  width: 50%;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
`;

const ArrowsContainer = styled.div`
  display: flex;
  width: 50%;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
`;

const Break = styled.div`
  flex-basis: 100%;
  height: 0;
`;

interface StyledNumpadButtonProps {
  readonly isActive?: boolean;
  readonly disabled?: boolean;
  readonly isInactive?: boolean;
}

const NumpadButton = styled.button<StyledNumpadButtonProps>(({ isActive, isInactive }) => ({
  flexBasis: '26%',
  height: '32px',
  margin: '4px',
  padding: 0,
  border: 0,
  backgroundColor: `${isActive ? colors.button.numPadActive : colors.button.base}`,
  color: ` ${colors.button.text}`,
  textAlign: 'center',
  fontSize: '14px',
  fontWeight: '500px',
  transition: 'backgroundColor .1s linear, opacity 0.17s linear',
  userSelect: 'none',
  opacity: isInactive ? 0.4 : 1,
  cursor: 'pointer',
  background: isActive ? colors.button.numPadActive : colors.button.base,

  '&:active': {
    backgroundColor: `${colors.button.active}`
  },

  '&:disabled': {
    opacity: '0.4',
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
}));

export const BackspaceButton = styled.button`
  user-select: none;
  width: 32px;
  height: 32px;
  margin: 0 0 0 auto;
  border: 0;

  svg {
    position: relative;
    top: -2px;
    left: -1px;
    width: 16px;
    height: 16px;
    vertical-align: middle;
  }
`;

const EnterButton = styled(NumpadButton)`
  background-color: ${modal.button.base};
  color: ${modal.button.color};
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;

  &:active {
    background-color: ${modal.button.hovered};
  }
`;

const DeleteButton = styled(NumpadButton)`
  background-color: ${modal.button.red};
  color: ${modal.button.color};
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;

  &:active {
    background-color: ${modal.button.red};
  }
`;

const blink = keyframes`
  from, to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
`;


const NumField = styled.div`
  display: flex;
  align-items: center;
  height: 35px;
  padding: 6px 0;
  margin: 0 20px;
  border-bottom: 1px solid #A4B9F4;
  font-size: 18px;

  span {
    max-width: 140px;
    overflow: hidden;
    display: inline-block;
    position: relative;
  }

  .blinking-cursor {
    display: inline-block;
    margin-left: 2px;
    width: 2px;
    height: 1em;
    background-color: rgb(1, 9, 87);
    animation: ${blink} 1s step-end infinite;
    vertical-align: bottom;
  }
`;

const ModalWithScroll = (left: number) => styled(Modal) ( // eslint-disable-line
  scrollbar,
  {
    minWidth: 0,
    maxWidth: '100%',
    left: `${left}px`,
    width: `calc(100% - ${left}px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: colors.base,
    zIndex: 1,
  },
);

const modalStyle = css`
  width: ${window.screen.width > 400 ? '100%' : '100%'};
  z-index: 1;
  h2 {
    display: flex;
    justify-content: space-between;
  }

  span {
    text-transform: capitalize;
  }
`;


interface InputProps {
  readonly top: number;
  readonly left: number;
  readonly height: number;
  readonly pose?: string;
}

interface StateProps {
  readonly startPoint: CoordinatePoint;
  readonly endPoint: CoordinatePoint;
  readonly previousStartPoint: CoordinatePoint;
  readonly unitOfMeasure: string;
  readonly currentGridSize: number;
  readonly inputSequence: string[];
  readonly currentValue: string;
  readonly currentAngleValue: string;
  readonly currentDirection: Direction | string | undefined;
  readonly isAngleInputEnabled: boolean;
  readonly walls: Immutable.Map<string, Wall | CircleWall>;
  readonly showNumpadModalWithNumpad: boolean;
  readonly selectedObjects: Immutable.List<string>;
  readonly drawInputSequence: string[];
  readonly isDrawing: boolean;
  readonly isShowingNumpadModal: boolean;
  readonly isDrawingInteriorWalls: boolean | null;
}

interface ActionProps {
  readonly setCurrentDirection: typeof drawWithKeypadActions.setCurrentDirection;
  readonly setAngleInput: typeof drawWithKeypadActions.setAngleInput;
  readonly addDrawInput: typeof drawWithKeypadActions.addDrawInput;
  readonly resetDrawInput: typeof drawWithKeypadActions.resetDrawInput;
  readonly hideNumpadModal: () => void;
  readonly setEndPoint: (endPoint: CoordinatePoint, center: boolean) => void;
  readonly finishSegment: () => void;
  readonly toggleDrawingInteriorWalls: (drawInteriorWalls: boolean) => void;
  readonly toggleDrawingPreshapes: (drawPreshapes: boolean) => void;
  readonly switchToEdit: typeof editModeActions.switchToEdit;
  readonly showMessage: (title: string, message: string | JSX.Element, onSubmit?: onSubmitEventType, onSubmitText?: string, isDissmisable?: boolean) => void;
  readonly removeSelectedObjects: () => void;
}

type Props = InputProps & StateProps & ActionProps;

type NumpadValue = string | '.' | ','

interface State {
  readonly value: string;
  readonly angleValue?: string;
  readonly direction?: Direction | string;
  readonly isTyping?: boolean;
}

const isValid = (string: string): boolean => /[1-9]/.test(string);

const Arrow: React.FC<{ direction: Direction | string }> = ({ direction }) => {
  switch (direction) {
    case Direction.Up: return <>↑</>;
    case Direction.Down: return <>↓</>;
    case Direction.Left: return <>←</>;
    case Direction.Right: return <>→</>;
    case Direction.UpLeft: return <>↖</>;
    case Direction.UpRight: return <>↗</>;
    case Direction.DownLeft: return <>↙</>;
    case Direction.DownRight: return <>↘</>;
    default: throw new UnreachableCaseError(direction);
  }
};

interface NumpadValueProps {
  value: NumpadValue;
  disabled?: boolean;
  isActive?: boolean;
  onClick: (value: string) => void;
}

const NumpadValue: React.FC<NumpadValueProps> = ({
  isActive, value, onClick, disabled,
}) => (
  <NumpadButton isActive={isActive} onClick={() => onClick(value)} disabled={disabled}>{value}</NumpadButton>
);

interface ArrowDirectionProps {
  direction: Direction;
  isActive?: boolean;
  disabled: boolean;
  onClick: (direction: Direction) => void;
}
const ArrowDirection: React.FC<ArrowDirectionProps> = ({
  direction, disabled, onClick, isActive
}) => (
  <NumpadButton isActive={isActive} onClick={() => onClick(direction)} disabled={disabled}>
    <Arrow direction={direction} />
  </NumpadButton>
);

const preventDefault = (ev: React.MouseEvent | React.TouchEvent): void => ev.preventDefault();

class NumpadMenuModal extends Component<Props, State> {
  private typingTimeout: number | undefined;

  public constructor(props: Props) {
    super(props);

    this.state = {
      isTyping: false,
    };
  }

  componentWillUnmount(): void {
    clearTimeout(this.typingTimeout);
  }

  private onClearStart = (): void => {
    const { resetDrawInput } = this.props;
    resetDrawInput();
  };

  private onClearEnd = (): void => {
    const { resetDrawInput } = this.props;
    resetDrawInput();
  };

  private handleValueChange = (newValue: string): void => {
    const { addDrawInput } = this.props;
    this.setState({ isTyping: true });

    // reset blinking after 1s of inactivity
    clearTimeout(this.typingTimeout);
    this.typingTimeout = window.setTimeout(() => {
      this.setState({ isTyping: false });
    }, 1000);

    addDrawInput(newValue);
  };

  private handleDirectionChange = (direction: Direction): void => {
    const {
      setEndPoint,
      unitOfMeasure,
      currentValue,
      currentGridSize,
      addDrawInput,
      isAngleInputEnabled,
      currentAngleValue,
      setCurrentDirection,
      endPoint,
      previousStartPoint
    } = this.props;

    setCurrentDirection(direction);

    if (isValid(currentValue)) {
      const { feet, inches } = parseString(currentValue);
      const pixels = unitOfMeasure === 'meters' ? metersToPixels(parseFloat(currentValue), currentGridSize) : feetToPixels(feet, inches);

      if (isAngleInputEnabled && currentAngleValue !== '0') {
        const newCoordinates = coordinatesByAngle(previousStartPoint, endPoint, pixels, +currentAngleValue, direction);
        setEndPoint({ ...newCoordinates }, false);
      } else if (!isAngleInputEnabled) {
        addDrawInput(direction);
        setEndPoint(movePointInDirection({ ...endPoint }, direction, pixels), false);
      } else {
        addDrawInput(direction);
      }
    }
  };

  private canFinish = (): boolean => {
    const {
      startPoint, endPoint, currentValue, currentDirection, currentAngleValue, isAngleInputEnabled
    } = this.props;
    if (isAngleInputEnabled && currentDirection) return currentValue !== '' && currentAngleValue !== '0' && currentDirection !== '';
    return startPoint.x !== endPoint.x || startPoint.y !== endPoint.y;
  };

  private handleEnter = (): void => {
    const {
      startPoint,
      showMessage,
    } = this.props;

    const isStartPointSet = startPoint && (startPoint.x === 0 || startPoint.y === 0);
    if (isStartPointSet) {
      showMessage(
        'Warning',
        'Please first tap on the grid to select a starting point to begin drawing a line.',
        () => { }, // eslint-disable-line @typescript-eslint/no-empty-function
        'Dismiss',
        false
      );
      return;
    }

    if (this.canFinish()) {
      const { finishSegment, resetDrawInput } = this.props;
      finishSegment();
      resetDrawInput();
    }
  };

  private switchToInteriorWallsMode = (): void => {
    const { switchToEdit, toggleDrawingInteriorWalls, toggleDrawingPreshapes } = this.props;
    toggleDrawingInteriorWalls(true);
    toggleDrawingPreshapes(false);
    switchToEdit();
  };

  private switchToEditMode = (): void => {
    const { switchToEdit, toggleDrawingInteriorWalls, toggleDrawingPreshapes } = this.props;
    toggleDrawingInteriorWalls(false);
    toggleDrawingPreshapes(false);
    switchToEdit();
  };

  private handleDelete = (): void => {
    const {
      selectedObjects,
      drawInputSequence,
      previousStartPoint,
      isDrawing,
      isShowingNumpadModal,
      removeSelectedObjects,
      resetDrawInput,
      setEndPoint,
      undoSegment
    } = this.props;

    if (selectedObjects.size > 0) {
      removeSelectedObjects();
      return;
    }

    if (isShowingNumpadModal) {
      if (drawInputSequence.length > 0) {
        resetDrawInput();
        setEndPoint(previousStartPoint, true);
      } else if (isDrawing) {
        undoSegment();
      }
    }
  };

  private handleAngleModeClick = () => {
    const { setAngleInput, currentValue, walls } = this.props;
    if (!walls.size) {
      toast.warning(messages.angleTriggerWithoutWalls);
      return;
    }

    if (!+currentValue) {
      toast.warning(messages.angleTriggerWithoutDistance);
      return;
    }
    setAngleInput();
  };

  public handleCloseClick = () => {
    const { hideNumpadModal } = this.props;
    hideNumpadModal();
  };

  public handleToggleClick = () => {
    const { toggleNumpadDisplay } = this.props;
    toggleNumpadDisplay();
  }

  private calculatedModalTop = () => {
    // top === topOfSketchDiv; height === heightOfSketchDiv, doesShowNumpad === do we show the full modal
    const {
      top, height, showNumpadModalWithNumpad
    } = this.props;

    // The modal including the numpad is 230 pixels tall. Without the numpad, it's down to just 50.
    const heightOfModal = (showNumpadModalWithNumpad) ? 230 : 50;
    const mobileNavOffset = (isMobileApplication() ? 70 : 0);

    // top + height = the bottom of the page
    // - heightOfModal sets the position of the modal to sit on the bottom of the screen.
    // - mobileReturnOffset moves the modal up a little further on mobile to fit the mobile app navigation menu
    return (top + height) - heightOfModal - mobileNavOffset;
  }

  public render = (): JSX.Element => {
    const {
      left,
      pose,
      currentValue,
      currentDirection,
      currentAngleValue,
      isAngleInputEnabled,
      inputSequence,
      showNumpadModalWithNumpad,
      isDrawingInteriorWalls,
      selectedObjects,
      isDrawing,
      drawInputSequence
    } = this.props;
    const { isTyping } = this.state;

    const deleteDisabled =
      selectedObjects.size === 0 &&
      drawInputSequence.length === 0 &&
      !isDrawing;

    const hasSelectedMode = isDrawingInteriorWalls !== null;

    // Active states
    const isInteriorActive = isDrawingInteriorWalls === true;
    const isExteriorActive = isDrawingInteriorWalls === false;
    // const isNoSelection = isDrawingInteriorWalls === null;
    const isInteriorDisabled = !hasSelectedMode || isDrawingInteriorWalls === false;
    const isExteriorDisabled = !hasSelectedMode || isDrawingInteriorWalls === true;

    const ModalAnimated = posed(ModalWithScroll(left))(appearFromTop);

    const directionDisabled = !isValid(currentValue);
    const finishDisabled = !this.canFinish();
    const lastInput = inputSequence[inputSequence.length - 1];
    const numbersDisabled = currentValue.length >= MAX_LENGTH;
    const styledCurrentAngleValue = ` ${currentAngleValue} °`;

    const numPadTop = this.calculatedModalTop();

    return (
      <ModalAnimated top={numPadTop} pose={pose} css={modalStyle}>
        <NumField>
          <span>
            {currentValue}
            {Object.values(Direction).includes(currentDirection as Direction) && (
              <Arrow direction={currentDirection as Direction} />
            )}
            {isAngleInputEnabled && currentAngleValue !== '' && styledCurrentAngleValue}
            {!isTyping && <span className="blinking-cursor" />}
          </span>
          <BackspaceButton
            onPointerDown={this.onClearStart}
            onPointerUp={this.onClearEnd}
            onTouchEnd={preventDefault}
          >
            <IconBackspace />
          </BackspaceButton>
          <Button onClick={this.handleToggleClick}>
            {showNumpadModalWithNumpad ? <IconNumpadHide /> : <IconNumpadShow />}
          </Button>
          <Button onClick={this.handleCloseClick}>
            <IconClose />
          </Button>
        </NumField>

        {showNumpadModalWithNumpad && (
          <NumpadContainer>
            <NumbersContainer>
              <NumpadValue isActive={lastInput === '7'} onClick={this.handleValueChange} value="7" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '8'} onClick={this.handleValueChange} value="8" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '9'} onClick={this.handleValueChange} value="9" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '4'} onClick={this.handleValueChange} value="4" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '5'} onClick={this.handleValueChange} value="5" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '6'} onClick={this.handleValueChange} value="6" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '1'} onClick={this.handleValueChange} value="1" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '2'} onClick={this.handleValueChange} value="2" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '3'} onClick={this.handleValueChange} value="3" disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '.'} onClick={this.handleValueChange} value="." disabled={numbersDisabled} />
              <NumpadValue isActive={lastInput === '0'} onClick={this.handleValueChange} value="0" disabled={numbersDisabled} />
              <NumpadValue isActive={isAngleInputEnabled} onClick={this.handleAngleModeClick} value="A" disabled={numbersDisabled} />
            </NumbersContainer>

            <ArrowsContainer>
              <EnterButton isActive={isExteriorActive} isInactive={isExteriorDisabled} onClick={this.switchToEditMode}>Exterior</EnterButton>
              <EnterButton isActive={isInteriorActive} isInactive={isInteriorDisabled} onClick={this.switchToInteriorWallsMode}>Interior</EnterButton>
              <DeleteButton onClick={this.handleDelete} disabled={deleteDisabled}>DELETE</DeleteButton>
              <Break />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowUpLeft'}
                direction={Direction.UpLeft}
                disabled={directionDisabled || isAngleInputEnabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowUp'}
                direction={Direction.Up}
                disabled={directionDisabled || isAngleInputEnabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowUpRight'}
                direction={Direction.UpRight}
                disabled={directionDisabled || isAngleInputEnabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowLeft'}
                direction={Direction.Left}
                disabled={directionDisabled}
              />
              <EnterButton onClick={this.handleEnter} disabled={finishDisabled}>Enter</EnterButton>
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowRight'}
                direction={Direction.Right}
                disabled={directionDisabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowDownLeft'}
                direction={Direction.DownLeft}
                disabled={directionDisabled || isAngleInputEnabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowDown'}
                direction={Direction.Down}
                disabled={directionDisabled || isAngleInputEnabled}
              />
              <ArrowDirection
                onClick={this.handleDirectionChange}
                isActive={lastInput === 'ArrowDownRight'}
                direction={Direction.DownRight}
                disabled={directionDisabled || isAngleInputEnabled}
              />
            </ArrowsContainer>
          </NumpadContainer>
        )}

      </ModalAnimated>
    );
  };
}

export default connect(
  (state: RootState): StateProps => ({
    isAngleInputEnabled: drawWithKeypadSelectors.getIsAngleInputEnabled(state),
    currentAngleValue: drawWithKeypadSelectors.getCurrentAngleValue(state),
    currentDirection: drawWithKeypadSelectors.getCurrentDirection(state),
    currentValue: drawWithKeypadSelectors.getCurrentValue(state),
    inputSequence: drawWithKeypadSelectors.getDrawInputSequence(state),
    startPoint: drawsSelectors.getStartPoint(state),
    endPoint: drawsSelectors.getEndPoint(state),
    previousStartPoint: drawsSelectors.getPreviousStartPoint(state),
    unitOfMeasure: settingsSelectors.getUnitOfMeasure(state),
    currentGridSize: settingsSelectors.getGridSizeInFeet(state),
    walls: wallSelectors.getAllWalls(state),
    showNumpadModalWithNumpad: numpadModalSelectors.doesShowNumpad(state),
    selectedObjects: editModeSelectors.getSelectedObjects(state),
    drawInputSequence: drawWithKeypadSelectors.getDrawInputSequence(state),
    isDrawing: editModeSelectors.isDrawingMode(state),
    isShowingNumpadModal: numpadModalSelectors.isShowing(state),
    isDrawingInteriorWalls: drawsSelectors.isDrawingInteriorWalls(state),
  }),
  {
    addDrawInput: drawWithKeypadActions.addDrawInput,
    setCurrentDirection: drawWithKeypadActions.setCurrentDirection,
    setAngleInput: drawWithKeypadActions.setAngleInput,
    resetDrawInput: drawWithKeypadActions.resetDrawInput,
    resetCurrentValue: drawWithKeypadActions.resetCurrentValue,
    resetCurrentDirection: drawWithKeypadActions.resetCurrentDirection,
    hideNumpadModal: numpadModalActions.hide,
    setEndPoint: drawActions.setEndPointWithoutSnapping,
    finishSegment: drawActions.finishSegment,
    toggleDrawingInteriorWalls: drawActions.toggleDrawingInteriorWalls,
    toggleDrawingPreshapes: drawActions.toggleDrawingPreshapes,
    switchToEdit: editModeActions.switchToEdit,
    showMessage: messageContainerActions.show,
    toggleNumpadDisplay: numpadModalActions.toggle,
    removeSelectedObjects: removeObjectsActions.removeSelectedObjects,
    removeLastInput: drawWithKeypadActions.removeLastInput,
    undo: historyActions.undo,
    undoSegment: drawActions.undoSegment,
  },
)(NumpadMenuModal);
